/**
 * One step of the feed: a phase glyph, the backend's own phrase, and — once
 * settled — how long it took. §5.3.
 *
 * The topic icon (`step.icon`, e.g. `ticket`) is deliberately NOT drawn here:
 * the mock shows only the phase glyph per row (spinner/check/exclamation),
 * and the topic icons are what the collapsed summary chip shows instead
 * (`ActivityFeed`'s own icon strip). Two readers of `icon` would just be one
 * glyph on the row and one in the chip, disagreeing about the same field.
 */

import type { Step } from './activityStore';
import { cn } from '../lib/cn';
import { stripEmoji as plain } from '../lib/emoji';

/** Only shown once the step settles, and only past this floor — a 0,1 с line
 *  on a fast lookup is noise, not information (§5.3). */
const MIN_SHOWN_MS = 300;

export function formatDuration(ms: number): string {
  const seconds = ms / 1000;
  const rounded = seconds >= 10 ? Math.round(seconds) : Math.round(seconds * 10) / 10;
  return `${String(rounded).replace('.', ',')} с`;
}

const KNOWN_PHASES = new Set(['running', 'done', 'failed']);

export default function ActivityRow({ step }: { step: Step }) {
  const phase = KNOWN_PHASES.has(step.phase) ? step.phase : 'unknown';
  const showDuration = step.ms !== null && step.ms >= MIN_SHOWN_MS && (phase === 'done' || phase === 'failed');

  return (
    <li className={cn('activity-row', `activity-row--${phase}`)}>
      {/* Keyed on phase so a running→done transition remounts this span and
          replays the crossfade + tick (§5.5.2) — the same trick BoardItem's
          own flash relies on, done here with a key instead of a JS timer. */}
      <span key={phase} className="activity-row__state" aria-hidden="true">
        {phase === 'running' ? (
          <span className="activity-row__spinner">⟳</span>
        ) : phase === 'done' ? (
          '✓'
        ) : phase === 'failed' ? (
          '!'
        ) : (
          '•'
        )}
      </span>
      <span className="activity-row__text">{plain(step.text)}</span>
      {showDuration ? <span className="activity-row__duration">{formatDuration(step.ms!)}</span> : null}
    </li>
  );
}
